import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { usePopper } from 'react-popper'
import Header from 'components/typography/Header'
import classNames from 'classnames'

import Fade from 'components/misc/Fade'
import styles from './styles/Tooltip.css'

const Tooltip = ({
  className,
  tooltipClassName,
  arrowClassName,
  refClassName,
  header,
  content,
  delay = 400,
  placement = 'top',
  open = false,
  children,
  ...rest
}) => {
  const popupTimeout = useRef()
  const [show, setShow] = useState(open)
  const [initialRender, setInitialRender] = useState(true)

  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const [arrowElement, setArrowElement] = useState(null)
  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed',
    modifiers: [
      {
        name: 'arrow',
        options: { element: arrowElement }
      },
      {
        name: 'offset',
        options: [0, 8]
      }
    ]
  })

  const handleMouseEnter = useCallback(_ => {
    popupTimeout.current = setTimeout(() => {
      setShow(true)
    }, delay)
  }, [delay])

  const handleMouseLeave = useCallback(_ => {
    if (popupTimeout.current) {
      window.clearTimeout(popupTimeout.current)
    }
    setShow(false)
  })

  useEffect(() => {
    setInitialRender(false)
  }, [])

  const wrapped = (
    <div
      className={classNames(refClassName, styles.reference)}
      ref={setReferenceElement}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      {children}
    </div>
  )

  if (initialRender) {
    return wrapped
  }

  return (
    <>
      {
        createPortal(
          (
            <Fade className={classNames(styles.fadeWrapper, tooltipClassName)} show={show}>
              <div
                className={classNames(className, styles.popperBase, styles.popper)}
                ref={setPopperElement}
                style={popperStyles.popper}
                {...attributes.popper}
              >
                {header && <Header level={5} className={styles.header}>{header}</Header>}
                {content}
                <div
                  className={classNames(styles.arrow, styles.arrowBlack, arrowClassName)}
                  ref={setArrowElement}
                  style={popperStyles.arrow}
                  data-popper-arrow
                />
              </div>
            </Fade>
          ),
          document.querySelector('#scite-tooltips')
        )
      }
      {wrapped}
    </>
  )
}

Tooltip.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]).isRequired,
  header: PropTypes.string,
  className: PropTypes.string,
  tooltipClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  delay: PropTypes.number,
  placement: PropTypes.string,
  open: PropTypes.bool
}

export default Tooltip
